import React, { useEffect, useState, useRef } from "react";
import Swal from 'sweetalert2';
import type { SweetAlertOptions } from 'sweetalert2';
import axios, { AxiosError } from "axios";
import { Progress, Button, Icon, Grid, List, Segment, Checkbox, CheckboxProps } from "semantic-ui-react";
import { DocumentViewModel, UploadDocumentViewModel, AttachmentChangeViewModel } from '../../../types/ServerDTO';
import styles from "./FileUpload.module.css";
import FileIcon from "./FileIcon";
import axiosInstance from "../../../helpers/axiosInstance";
import { utils } from '../../../helpers/utils';

interface FileUploadProps {
  uniqueKey: string;
  caseId: number;
  title?: string;
  readOnly?: boolean;
  single?: boolean;
  apiUrl: string;
  handleUpdateDocument: Function;
  serverAttachments?: UploadDocumentViewModel;
  sectionClassName?: string;
  layout?: string;
  maxFileSize?: number;
  isUpdated?: boolean;
}

interface FileWithProgress {
  file: File;
  progress: number;
  attachmentGUID: string | null;
  error?: string;
  checked?: boolean;
}

const FileUploadMultiple: React.FC<FileUploadProps> = ({
  uniqueKey,
  apiUrl,
  caseId = 0,
  title = "Attachments",
  handleUpdateDocument,
  readOnly = false,
  single = false,
  serverAttachments,
  sectionClassName = 'attachment-section',
  layout,
  maxFileSize = 25,
  isUpdated,
}) => {
  const allowedFileTypes = ["image/jpeg", "image/png"];
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [documents, setDocuments] = useState<DocumentViewModel[]>(serverAttachments?.documents || []);
  const [uploadVersion, setUploadVersion] = useState<number>(1)
  const [error, setError] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isUpdated) {
      setError('');
    }
  }, [isUpdated]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!readOnly && event.dataTransfer.files) {
      const droppedFiles: FileWithProgress[] = [];
      let isShowError = false;
      Array.from(event.dataTransfer.files).map((file) => {
        let error = undefined;
        if ((file.size / 1048576) > maxFileSize) {
          error = 'Please choose a file that is smaller than ' + maxFileSize + ' MB and try again.';
        }
        if (error === undefined) {
          droppedFiles.push({ file, attachmentGUID: null, error, progress: 0 } as FileWithProgress);
        } else {
          isShowError = true;
          setError(error);
        }
      });
      if (!isShowError) {
        setError('');
      }
      setFiles(single ? droppedFiles.slice(0, 1) : [...files, ...droppedFiles]);
      droppedFiles.forEach(uploadFile);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles: FileWithProgress[] = [];
      let isShowError = false;
      Array.from(event.target.files).map((file: File) => {
        let error = undefined;
        if ((file.size / 1048576) > maxFileSize) {
          error = 'Please choose a file that is smaller than ' + maxFileSize + ' MB and try again.';
        }
        if (error === undefined) {
          selectedFiles.push({ file, progress: 0, error, checked: error === undefined ? true : false } as FileWithProgress);
        } else {
          isShowError = true;
          setError(error);
        }
      });
      if (!isShowError) {
        setError('');
      }
      setFiles(single ? selectedFiles.slice(0, 1) : [...files, ...selectedFiles]);
      selectedFiles.forEach(uploadFile);
    }
  };

  const uploadFile = async (fileExtended: FileWithProgress) => {
    try {
      if (fileExtended.error === undefined) {
        const formData = new FormData();
        formData.append("file", fileExtended.file);
        formData.append("caseId", caseId.toString());

        await axiosInstance.post(apiUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            let newTotal = total ? total : 0;
            const progress = Math.round((loaded / newTotal) * 100);
            if (progress < 100) {
              updateFileProgress(fileExtended, progress);
            }
          }
        }).then(({ data, status, statusText }) => {
          if (status === 200) {
            fileExtended.attachmentGUID = data;
            updateFileProgress(fileExtended, 100);
            if (handleUpdateDocument) {
              handleUpdateDocument(
                { attachmentGUID: data, isMarkedForDelete: false } as AttachmentChangeViewModel,
                false
              );
            }
          }
        })
          .catch((error) => {
            updateFileError(fileExtended, error.message);
          }).finally(() => { });
      }
    } catch (error: any) {
      updateFileError(fileExtended, error.message);
    } finally {
      setUploadVersion((prev: number) => ++prev);
    }
  };

  const updateFileProgress = (fileExtended: FileWithProgress, progress: number) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) => (f.file === fileExtended.file ? { ...fileExtended, progress } : f))
    );
  };

  const updateFileError = (fileExtended: FileWithProgress, error: string) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) => (f.file === fileExtended.file ? { ...fileExtended, error, progress: 100 } : f))
    );
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>, fileExtended: FileWithProgress, data: CheckboxProps) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) => (f.file === fileExtended.file ? { ...fileExtended, checked: data.checked } : f))
    );
    if (handleUpdateDocument) {
      handleUpdateDocument(
        { attachmentGUID: fileExtended.attachmentGUID, isMarkedForDelete: data.checked } as AttachmentChangeViewModel,
        true
      );
    }
  };

  const removeNewFile = (fileExtended: any, documentId: number | null) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.file !== fileExtended.file));
    if (handleUpdateDocument) {
      handleUpdateDocument(
        { attachmentGUID: fileExtended.attachmentGUID, isMarkedForDelete: true } as AttachmentChangeViewModel,
        true
      );
    }
  };

  const removeExistingFile = (attachmentGUID: any, documentId: number | null) => {
    setDocuments((prevFiles: DocumentViewModel[]) => {
      const filteredDocument = prevFiles.filter((f) => f.attachmentGUID !== attachmentGUID);
      return filteredDocument;
    });
    if (handleUpdateDocument) {
      handleUpdateDocument(
        { attachmentGUID, isMarkedForDelete: true } as AttachmentChangeViewModel,
        false
      );
    }
  };

  const handleDelete = (onSuccess: (fileExtended: FileWithProgress | string, documentId: number | null) => void,
    fileExtended: FileWithProgress | string, documentId: number | null) => {
    const options: SweetAlertOptions = {
      title: 'Are you sure you want to delete?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    };

    Swal.fire(options).then((result) => {
      if (result.isConfirmed) {
        onSuccess(fileExtended, documentId);
      }
    });
  };

  const handleDeleteClick = (fileExtended: FileWithProgress | string, documentId: number | null,
    onSuccess: (fileExtended: FileWithProgress | string, documentId: number | null) => void) => {
    handleDelete(onSuccess, fileExtended, documentId);
  };

  const retryUpload = (fileExtended: FileWithProgress) => {
    uploadFile({ ...fileExtended, progress: 0, error: undefined });
  };

  const downloadFile = async (downloadLink: string, fileName: string) => {
    try {
      const response = await axiosInstance.get(downloadLink, { responseType: 'arraybuffer' });
      utils.DownloadBlobFileWithContentDisposition(response, fileName);

    } catch (error) {
      console.error('Error downloading file:', error);
      console.error("Global error handler:", error);
      const axError = error as AxiosError;
      if (axError && axError?.response?.status === 500) {
        // Show a polite error message using SweetAlert2
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "An error occurred while processing your request.",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const humanFileSize = (bytes: number, si = true, dp = 1) => {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }

    const units = si
      ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
      bytes /= thresh;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
  }

  return (
    <>
      {layout && layout === 'simple' && (
        <>
          <List relaxed className="attachments-list">
            {documents.length > 0 && documents.map((fileExtended: DocumentViewModel, index: number) => (
              <List.Item key={index}>
                <List.Content>
                  <Grid style={{ padding: '0.5rem 0 0 0' }}>
                    <Grid.Column width={16} style={{ display: 'flex', height: '35px' }}>
                      <span>{fileExtended.fileName}</span>
                      <span>
                        <Icon
                          name="trash"
                          color={'red'}
                          className={`right floated ${styles.deleteIcon}`}
                          onClick={() => fileExtended.attachmentGUID && handleDeleteClick(fileExtended.attachmentGUID, fileExtended.documentId, removeExistingFile)}
                        />
                      </span>
                    </Grid.Column>
                  </Grid>
                </List.Content>
              </List.Item>
            ))
            }
            {files.length > 0 && files.map((fileExtended: FileWithProgress, index) => {
              return (
                <List.Item key={index}>
                  <List.Content>
                    <Grid style={{ padding: '0.5rem 0 0 0' }}>
                      <Grid.Column width={16} style={{ display: 'flex', height: '35px' }}>
                        <span>{fileExtended.file.name}</span>
                        <span style={{ marginLeft: '20px' }}>{humanFileSize(fileExtended.file.size)}</span>
                        <span>
                          <Icon
                            name="trash"
                            color={'red'}
                            className={`right floated ${styles.deleteIcon}`}
                            onClick={() => handleDeleteClick(fileExtended, null, removeNewFile)}
                          />
                        </span>
                        {fileExtended.progress > 0 && fileExtended.progress < 100 &&
                          <span style={{ width: '30%', marginLeft: '10px', marginTop: '6px' }}>
                            <Progress
                              fluid
                              className={styles.progressBar}
                              percent={fileExtended.progress}
                              size="small"
                              success={fileExtended.attachmentGUID ? true : false}
                              error={fileExtended.error ? true : false}
                            ></Progress>
                          </span>
                        }
                        {fileExtended.error && (
                          <span style={{ color: 'red' }}>{fileExtended.error}</span>
                        )}
                      </Grid.Column>
                    </Grid>
                  </List.Content>
                </List.Item>
              )
            }
            )}
          </List>
          <input
            key={`uploadCtrl`}
            name={uniqueKey}
            type="file"
            multiple={!single}
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
            disabled={readOnly}
            onClick={(event: any) => {
              event.target.value = null
            }}
          />
          <div className="mt-1"><Icon name="paperclip" /><span onClick={() => fileInputRef.current?.click()} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Attach files</span></div>
          <p>Maximum file size {maxFileSize} MB</p>
          {error && (
            <p style={{ color: 'red' }}>{error}</p>
          )}
        </>
      )}
      {layout === undefined && (
        <section className={sectionClassName}>
          <input
            key={`uploadCtrl`}
            name={uniqueKey}
            type="file"
            multiple={!single}
            ref={fileInputRef}
            onChange={handleFileChange}
            disabled={readOnly}
            onClick={(event: any) => {
              event.target.value = null
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            style={{
              position: 'absolute',
              zIndex: '-1'
            }}
          />
          <div
            className={`${styles.uploadContainer} ${isInputFocused ? styles.focused : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {readOnly ? (
              <span>Read-only mode</span>
            ) : (
              <>
                <Button
                  style={{ padding: '.78571429em 1.5em .78571429em' }}
                  size="tiny"
                  as="label"
                  htmlFor={uniqueKey}
                  type="button"
                  className="blue"
                >
                  Select File(s)
                  <Icon name="upload" />
                </Button>
              </>
            )}
          </div>
          <p>Maximum file size {maxFileSize} MB</p>
          {(documents.length > 0 || files.length > 0) && (
            <Segment>
              <List divided relaxed>
                {(documents || []).map((fileExtended: DocumentViewModel, index: number) => (
                  <List.Item key={index}>
                    <List.Icon size='large' verticalAlign='middle'>
                      <FileIcon fileName={fileExtended.fileName} />
                    </List.Icon>
                    <List.Content>
                      <Grid style={{ padding: '0.5rem 0 0 0' }}>
                        <Grid.Column width={14} style={{ padding: '1rem' }}>
                          <strong>{fileExtended.fileName}</strong>
                        </Grid.Column>
                        <Grid.Column width={2} style={{ padding: '1rem' }}>
                          <Icon
                            name="trash"
                            color={'red'}
                            title={`Delete - ${fileExtended.fileName}`}
                            className={`right floated ${styles.deleteIcon}`}
                            onClick={() => fileExtended.attachmentGUID && handleDeleteClick(fileExtended.attachmentGUID, fileExtended.documentId, removeExistingFile)}
                            style={{ margin: 0 }}
                          />
                          {fileExtended.downloadsUrl &&
                            <Icon
                              color={'teal'}
                              title={`Download - ${fileExtended.fileName}`}
                              name="download"
                              className={`right floated ${styles.deleteIcon}`}
                              onClick={() => downloadFile(fileExtended.downloadsUrl, fileExtended.fileName)}
                              style={{ margin: 0, marginRight: '0.5rem' }}
                            />
                          }
                        </Grid.Column>
                      </Grid>
                    </List.Content>
                  </List.Item>
                ))
                }
                {files.map((fileExtended: FileWithProgress, index) => (
                  <List.Item key={index}>
                    <List.Icon size='large' verticalAlign='middle'>
                      <FileIcon fileName={fileExtended.file.name} />
                    </List.Icon>
                    <List.Content>
                      <Grid style={{ padding: '0.5rem 0 0 0' }}>
                        <Grid.Column width={14} style={{ padding: '1rem' }}>
                          <strong>{fileExtended.file.name} - {humanFileSize(fileExtended.file.size)}
                            &nbsp;&nbsp;<Icon name="cloud upload" color={fileExtended.error !== undefined ? "red" : (fileExtended.progress === 100 ? "green" : "grey")} />
                          </strong>
                        </Grid.Column>
                        <Grid.Column width={2} style={{ padding: '1rem' }}>
                          <Icon
                            name="trash"
                            color={'red'}
                            className={`right floated ${styles.deleteIcon}`}
                            onClick={() => handleDeleteClick(fileExtended, null, removeNewFile)}
                          />
                        </Grid.Column>
                      </Grid>
                      {fileExtended.progress > 0 && fileExtended.progress < 100 &&
                        <List.Description>
                          <Progress
                            fluid
                            style={{ width: '98%' }}
                            className={styles.progressBar}
                            percent={fileExtended.progress}
                            size="tiny"
                            success={fileExtended.attachmentGUID ? true : false}
                            error={fileExtended.error ? true : false}
                          ></Progress>
                        </List.Description>
                      }
                      {fileExtended.error && (
                        <span style={{ color: 'red' }}>{fileExtended.error}</span>
                      )}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </Segment>
          )}
          {error && (
            <p style={{ color: 'red' }}>{error}</p>
          )}
        </section>
      )}
    </>
  );
};

export default FileUploadMultiple;
